import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Button,
  Card,
  CardBody,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
  Switch,
} from '@nextui-org/react';
import {
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  Images,
  Pencil,
  Plus,
  Search,
  Sliders as SlidersIcon,
  Trash2,
  XCircle,
} from 'lucide-react';
import adminSlidersService, {
  AdminSlider,
  SliderLocationDefinition,
} from '../../services/adminSlidersService';

const ACCENT = '#3fced1';
const CUSTOM_LOCATION_KEY = '__custom__';

const inputClasses = {
  inputWrapper: 'bg-zinc-800 border border-zinc-700',
  input: 'text-white',
  label: 'text-gray-400',
};

const selectClasses = {
  trigger: 'bg-zinc-800 border border-zinc-700',
  label: 'text-gray-400',
  value: 'text-white',
};

const FeedbackBanner: React.FC<{
  message: string | null;
  type: 'success' | 'error';
  onClose: () => void;
}> = ({ message, type, onClose }) => {
  if (!message) return null;
  const isSuccess = type === 'success';
  return (
    <div
      className={`mb-4 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${
        isSuccess
          ? 'border-[#3fced1]/40 bg-[#3fced1]/10 text-[#3fced1]'
          : 'border-red-500/40 bg-red-500/10 text-red-400'
      }`}
    >
      {isSuccess ? <CheckCircle2 size={18} className="mt-0.5 shrink-0" /> : <XCircle size={18} className="mt-0.5 shrink-0" />}
      <span className="flex-1">{message}</span>
      <button type="button" onClick={onClose} aria-label="Hinweis schließen" className="opacity-70 hover:opacity-100">✕</button>
    </div>
  );
};

/** Formular-Zustand für Anlegen & Bearbeiten eines Sliders. */
interface SliderFormState {
  id: string | null; // null = neu anlegen
  name: string;
  locationKey: string; // Definition-Location oder CUSTOM_LOCATION_KEY
  customLocation: string;
  order: string;
  autoSlideDuration: string;
}

const emptyForm: SliderFormState = {
  id: null,
  name: '',
  locationKey: '',
  customLocation: '',
  order: '',
  autoSlideDuration: '',
};

const AdminSlidersPage: React.FC = () => {
  const navigate = useNavigate();
  const [sliders, setSliders] = useState<AdminSlider[]>([]);
  const [slideCounts, setSlideCounts] = useState<Record<string, number>>({});
  const [definitions, setDefinitions] = useState<SliderLocationDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const [form, setForm] = useState<SliderFormState | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [sliderList, defs, slides] = await Promise.all([
        adminSlidersService.listSliders(),
        adminSlidersService.getLocationDefinitions(),
        adminSlidersService.listAllSlides(),
      ]);
      setSliders([...sliderList].sort((a, b) => a.name.localeCompare(b.name, 'de')));
      setDefinitions(defs);
      const counts: Record<string, number> = {};
      slides.forEach((s) => {
        counts[s.sliderId] = (counts[s.sliderId] ?? 0) + 1;
      });
      setSlideCounts(counts);
    } catch (err) {
      setError(adminSlidersService.getErrorMessage(err, 'Slider konnten nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredSliders = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sliders;
    return sliders.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.location.toLowerCase().includes(q) ||
        (s.description ?? '').toLowerCase().includes(q)
    );
  }, [sliders, search]);

  const definitionByLocation = useMemo(() => {
    const map: Record<string, SliderLocationDefinition> = {};
    definitions.forEach((d) => {
      map[d.location] = d;
    });
    return map;
  }, [definitions]);

  const openCreate = () => setForm({ ...emptyForm });

  const openEdit = (slider: AdminSlider) => {
    const isKnown = Boolean(definitionByLocation[slider.location]);
    setForm({
      id: slider.id,
      name: slider.name,
      locationKey: isKnown ? slider.location : CUSTOM_LOCATION_KEY,
      customLocation: isKnown ? '' : slider.location,
      order: String(slider.order ?? 0),
      autoSlideDuration: slider.autoSlideDuration != null ? String(slider.autoSlideDuration) : '',
    });
  };

  const resolveFormLocation = (f: SliderFormState): string =>
    f.locationKey === CUSTOM_LOCATION_KEY ? f.customLocation.trim() : f.locationKey;

  const handleSave = async () => {
    if (!form) return;
    const location = resolveFormLocation(form);
    if (!form.name.trim() || !location) {
      setError('Name und Location sind erforderlich.');
      return;
    }
    const order = form.order.trim() === '' ? null : parseInt(form.order, 10);
    const autoSlideDuration =
      form.autoSlideDuration.trim() === '' ? null : parseInt(form.autoSlideDuration, 10);
    if (order !== null && (isNaN(order) || order < 0)) {
      setError('Reihenfolge muss eine Zahl ≥ 0 sein.');
      return;
    }
    if (autoSlideDuration !== null && (isNaN(autoSlideDuration) || autoSlideDuration < 1)) {
      setError('Auto-Slide-Dauer muss mindestens 1 Sekunde sein.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (form.id) {
        await adminSlidersService.updateSlider(form.id, {
          name: form.name.trim(),
          location,
          order,
          autoSlideDuration,
        });
        setFeedback('Slider aktualisiert.');
      } else {
        const created = await adminSlidersService.createSlider({
          name: form.name,
          location,
          order,
          autoSlideDuration,
        });
        setFeedback('Slider erstellt.');
        if (created?.id) {
          setForm(null);
          navigate(`/admin/slider/${created.id}`);
          return;
        }
      }
      setForm(null);
      await load();
    } catch (err) {
      setError(adminSlidersService.getErrorMessage(err, 'Speichern fehlgeschlagen.'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (slider: AdminSlider) => {
    // Optimistisch umschalten, bei Fehler zurückrollen
    setSliders((prev) => prev.map((s) => (s.id === slider.id ? { ...s, active: !s.active } : s)));
    try {
      await adminSlidersService.toggleSlider(slider.id);
    } catch (err) {
      setSliders((prev) => prev.map((s) => (s.id === slider.id ? { ...s, active: slider.active } : s)));
      setError(adminSlidersService.getErrorMessage(err, 'Status konnte nicht geändert werden.'));
    }
  };

  const handleDelete = async (slider: AdminSlider) => {
    if (confirmDeleteId !== slider.id) {
      setConfirmDeleteId(slider.id);
      return;
    }
    setConfirmDeleteId(null);
    try {
      await adminSlidersService.deleteSlider(slider.id);
      setSliders((prev) => prev.filter((s) => s.id !== slider.id));
      setFeedback(`"${slider.name}" gelöscht.`);
    } catch (err) {
      setError(adminSlidersService.getErrorMessage(err, 'Slider konnte nicht gelöscht werden.'));
    }
  };

  const selectedDefinition =
    form && form.locationKey && form.locationKey !== CUSTOM_LOCATION_KEY
      ? definitionByLocation[form.locationKey]
      : null;

  return (
    <>
      <Helmet>
        <title>Slider | Admin | DartCloud</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <Button
            as={RouterLink}
            to="/admin"
            isIconOnly
            variant="bordered"
            className="border-zinc-700 text-gray-300"
            aria-label="Zurück zum Admin-Dashboard"
          >
            <ArrowLeft size={18} />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="flex items-center gap-2 text-2xl font-extrabold tracking-tight text-white">
              <SlidersIcon size={22} style={{ color: ACCENT }} />
              Slider
            </h1>
            <p className="text-sm text-gray-400">
              Werbe- & Hero-Slider auf allen Seiten verwalten — Slides pro Position anlegen.
            </p>
          </div>
          <Button
            className="bg-[#3fced1] font-semibold text-black"
            startContent={<Plus size={16} />}
            onPress={openCreate}
          >
            Neuer Slider
          </Button>
        </div>

        <FeedbackBanner message={feedback} type="success" onClose={() => setFeedback(null)} />
        <FeedbackBanner message={error} type="error" onClose={() => setError(null)} />

        <Input
          value={search}
          onValueChange={setSearch}
          placeholder="Slider suchen (Name, Location, Beschreibung)…"
          startContent={<Search size={16} className="text-gray-500" />}
          classNames={{ ...inputClasses, base: 'mb-5' }}
          isClearable
          onClear={() => setSearch('')}
        />

        {loading ? (
          <div className="flex min-h-[300px] items-center justify-center">
            <Spinner size="lg" color="primary" />
          </div>
        ) : (
          <div className="space-y-3">
            {filteredSliders.map((slider) => (
              <Card key={slider.id} className="border border-zinc-800 bg-zinc-900">
                <CardBody className="flex flex-row flex-wrap items-center gap-3 p-4">
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/slider/${slider.id}`)}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#3fced1]/10 text-[#3fced1]">
                      <Images size={18} />
                    </span>
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-base font-bold text-white">{slider.name}</span>
                        <span className="shrink-0 rounded-full bg-zinc-800 px-2 py-0.5 font-mono text-[11px] text-gray-400">
                          {slider.location}
                        </span>
                        {!slider.active && (
                          <span className="shrink-0 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-bold text-amber-400">
                            Inaktiv
                          </span>
                        )}
                      </span>
                      <span className="block truncate text-xs text-gray-500">
                        {slideCounts[slider.id] ?? 0} Slides · Reihenfolge: {slider.order}
                        {slider.autoSlideDuration != null && ` · Auto-Slide: ${slider.autoSlideDuration}s`}
                      </span>
                      {slider.description && (
                        <span className="mt-0.5 block text-xs text-gray-600 line-clamp-1">
                          {slider.description}
                        </span>
                      )}
                    </span>
                  </button>

                  <div className="flex shrink-0 items-center gap-2">
                    <Switch
                      size="sm"
                      isSelected={slider.active}
                      onValueChange={() => handleToggle(slider)}
                      aria-label="Slider aktiv"
                    />
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      aria-label="Slider bearbeiten"
                      className="text-gray-400"
                      onPress={() => openEdit(slider)}
                    >
                      <Pencil size={15} />
                    </Button>
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      aria-label="Slider löschen"
                      className={confirmDeleteId === slider.id ? 'text-red-400' : 'text-gray-500 hover:text-red-400'}
                      onPress={() => handleDelete(slider)}
                    >
                      <Trash2 size={15} />
                    </Button>
                    <ChevronRight size={16} className="text-gray-600" />
                  </div>
                  {confirmDeleteId === slider.id && (
                    <p className="w-full text-xs text-red-400">
                      Wirklich löschen? Erneut auf den Papierkorb klicken zum Bestätigen.
                    </p>
                  )}
                </CardBody>
              </Card>
            ))}

            {filteredSliders.length === 0 && (
              <div className="flex flex-col items-center gap-3 py-16 text-center">
                <SlidersIcon size={40} className="text-zinc-700" />
                <p className="text-gray-500">
                  {search ? 'Keine Slider gefunden.' : 'Noch keine Slider vorhanden.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Anlegen/Bearbeiten-Modal */}
      <Modal isOpen={form !== null} onClose={() => setForm(null)} placement="center" scrollBehavior="inside">
        <ModalContent className="border border-zinc-800 bg-zinc-900">
          <ModalHeader className="text-white">
            {form?.id ? 'Slider bearbeiten' : 'Neuer Slider'}
          </ModalHeader>
          {form && (
            <ModalBody>
              <Input
                autoFocus
                label="Name"
                placeholder='z.B. "Startseite - Hero Slider"'
                value={form.name}
                onValueChange={(v) => setForm({ ...form, name: v })}
                classNames={inputClasses}
              />
              <Select
                label="Location (Anzeigeposition)"
                selectedKeys={form.locationKey ? [form.locationKey] : []}
                onSelectionChange={(keys) => {
                  const key = String(Array.from(keys)[0] ?? '');
                  setForm({ ...form, locationKey: key });
                }}
                classNames={selectClasses}
              >
                {[
                  ...definitions.map((d) => (
                    <SelectItem key={d.location} textValue={`${d.name} (${d.location})`}>
                      <div>
                        <p className="text-sm">{d.name}</p>
                        <p className="font-mono text-xs text-gray-500">{d.location}</p>
                      </div>
                    </SelectItem>
                  )),
                  <SelectItem key={CUSTOM_LOCATION_KEY} textValue="Eigene Location eingeben…">
                    Eigene Location eingeben…
                  </SelectItem>,
                ]}
              </Select>
              {form.locationKey === CUSTOM_LOCATION_KEY && (
                <Input
                  label="Eigene Location"
                  placeholder="z.B. mein_neuer_bereich"
                  value={form.customLocation}
                  onValueChange={(v) => setForm({ ...form, customLocation: v })}
                  classNames={inputClasses}
                />
              )}
              {selectedDefinition && (
                <p className="rounded-lg bg-zinc-800/60 px-3 py-2 text-xs text-gray-400">
                  {selectedDefinition.description}
                </p>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Reihenfolge (optional)"
                  type="number"
                  value={form.order}
                  onValueChange={(v) => setForm({ ...form, order: v })}
                  classNames={inputClasses}
                />
                <Input
                  label="Auto-Slide (Sek., optional)"
                  type="number"
                  description="Leer = Standard (5s)"
                  value={form.autoSlideDuration}
                  onValueChange={(v) => setForm({ ...form, autoSlideDuration: v })}
                  classNames={inputClasses}
                />
              </div>
            </ModalBody>
          )}
          <ModalFooter>
            <Button variant="light" onPress={() => setForm(null)}>Abbrechen</Button>
            <Button
              isLoading={saving}
              isDisabled={!form || !form.name.trim() || !resolveFormLocation(form)}
              className="bg-[#3fced1] font-semibold text-black"
              onPress={handleSave}
            >
              {form?.id ? 'Speichern' : 'Anlegen'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AdminSlidersPage;
